import React, { Component } from "react";
import { NavLink, Link, withRouter } from "react-router-dom";
import rizeeLogo from "../../../../images/registrations/logo-blue.png";
import { clearFoundationStudent } from "../../foundationSession";
import CommonHeader from "../../../components/navbars/CommonHeader";
import "../../theme/_fdn-theme.scss";
import "./_foundationLayout.scss";

/**
 * Self-contained shell for the IIT Foundation student portal.
 *
 * Independent of the rizee student app: no shared Navbars / AsideNavbar, no
 * cookies / localStorage, no `studenttoken` gate. Sidebar open/close is component
 * state only. Carries Rizee branding with an "IIT Foundation" sub-brand.
 *
 * The shell carries .fdn-theme, so every page inside gets the foundation design
 * tokens and building blocks (theme/_fdn-theme.scss).
 */

const ICON = {
  dashboard: <><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></>,
  tests: <><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>,
  practice: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  create: <><circle cx="12" cy="12" r="10" /><path d="M12 8v8" /><path d="M8 12h8" /></>,
  videos: <><rect x="2" y="4" width="14" height="16" rx="2" /><path d="m22 8-6 4 6 4V8Z" /></>,
  notes: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><path d="M14 2v6h6" /></>,
  shortNotes: <><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2Z" /></>,
  analysis: <><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></>,
  errors: <><circle cx="12" cy="12" r="10" /><path d="m15 9-6 6" /><path d="m9 9 6 6" /></>,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  logout: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="m16 17 5-5-5-5" /><path d="M21 12H9" /></>,
  profile: <><path d="M20 21a8 8 0 0 0-16 0" /><circle cx="12" cy="7" r="4" /></>,
  caret: <><path d="m6 9 6 6 6-6" /></>,
  rocket: <><path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09Z" /><path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2Z" /><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" /><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" /></>,
};

const Svg = ({ children }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {children}
  </svg>
);

// Each item carries its own colour (tone) - the menu doubles as a colour key
// for the matching page headers.
const NAV_ITEMS = [
  { to: "/iit-foundation/student-dashboard", label: "Dashboard", exact: true, icon: ICON.dashboard, tone: "violet" },
  { to: "/student/iit-foundation-course/tests", label: "Tests", icon: ICON.tests, tone: "blue" },
  { to: "/iit-foundation/create-exam", label: "Create Exam", icon: ICON.create, tone: "orange" },
  // Practice hidden for now.
  // { to: "/student/iit-foundation-course/practice", label: "Practice", icon: ICON.practice },
  // Videos hidden for now.
  // { to: "/student/iit-foundation-course/videos", label: "Videos", icon: ICON.videos },
  // Notes placeholder hidden; the working short-notes page is now labelled "Notes".
  // { to: "/student/iit-foundation-course/notes", label: "Notes", icon: ICON.notes },
  { to: "/iit-foundation/short-notes", label: "Notes", icon: ICON.notes, tone: "pink" },
  { to: "/iit-foundation/theory", label: "Theory", icon: ICON.shortNotes, tone: "teal" },
  // Analysis hidden for now.
  // { to: "/student/iit-foundation-course/analysis", label: "Analysis", icon: ICON.analysis },
  { to: "/student/iit-foundation-course/error-exams", label: "Error Exams", icon: ICON.errors, tone: "red" },
];

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

class FoundationLayout extends Component {
  // open      -> mobile drawer visibility
  // collapsed -> desktop icon-rail (labels hidden)
  // userMenu  -> top-navbar profile/logout dropdown
  state = { open: false, collapsed: false, userMenu: false };

  toggle = () => {
    const isMobile =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(max-width: 992px)").matches;
    if (isMobile) this.setState((s) => ({ open: !s.open }));
    else this.setState((s) => ({ collapsed: !s.collapsed }));
  };
  close = () => this.setState({ open: false });

  toggleUserMenu = () => this.setState((s) => ({ userMenu: !s.userMenu }));
  closeUserMenu = () => this.setState({ userMenu: false });
  handleLogout = () => { clearFoundationStudent(); this.closeUserMenu(); };

  // The header names the page from the menu, so pages don't have to pass a title.
  currentItem = () => {
    const path = (this.props.location && this.props.location.pathname) || "";
    return NAV_ITEMS.find((it) => path === it.to || path.indexOf(it.to + "/") === 0) || null;
  };

  render() {
    const { open, collapsed, userMenu } = this.state;
    // Prefer the full student object; fall back to studentName for older callers.
    const { student, studentName, title, children } = this.props;
    const s = student || {};
    const name = (s.name || studentName || "").trim() || "Student";
    const firstName = name.split(/\s+/)[0];
    const initial = name.charAt(0).toUpperCase();
    const email = s.email_mask || s.email || "";
    const classLabel = s.class || (s.class_num ? `Class ${s.class_num}` : "");
    const current = this.currentItem();

    return (
      <div className={`fdn-shell fdn-theme ${open ? "is-open" : ""} ${collapsed ? "is-collapsed" : ""}`}>
        <aside className="fdn-sidebar">
          <div className="fdn-brand">
            <img src={rizeeLogo} alt="Rizee" className="fdn-brand-logo" />
            <span className="fdn-brand-mini" aria-hidden="true">R</span>
            <span className="fdn-brand-tag">
              <Svg>{ICON.rocket}</Svg>
              IIT Foundation
            </span>
          </div>

          <div className="fdn-nav-label">Let's learn</div>
          <nav className="fdn-nav">
            {NAV_ITEMS.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                exact={it.exact}
                className={`fdn-nav-item tone-${it.tone}`}
                activeClassName="is-active"
                onClick={this.close}
                title={it.label}
              >
                <span className="fdn-nav-icon"><Svg>{it.icon}</Svg></span>
                <span className="fdn-nav-text">{it.label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="fdn-sidebar-card">
            <span className="fdn-sidebar-card__emoji" aria-hidden="true">🚀</span>
            <strong>Keep going, {firstName}!</strong>
            <span>A little practice every day adds up.</span>
          </div>
          <div className="fdn-sidebar-foot">Powered by Rizee</div>
        </aside>

        {/* click-away overlay, mobile only */}
        <div className="fdn-overlay" onClick={this.close} />

        <div className="fdn-main">
          {/* The shared student header (its own colour). Foundation has its own
              login, so it keeps its own user menu instead of the student one. */}
          <CommonHeader
            className="fdn-common-header"
            color="auto"
            tone="light"
            container={false}
            showUserMenu={false}
            onToggleSidebar={this.toggle}
            title={title || (current && current.label) || "Dashboard"}
            left={
            <div className="fdn-header-right ml-auto d-flex align-items-center">
              <span className="fdn-header-hello text-white mr-3 d-none d-md-inline">{greeting()}, {firstName} 👋</span>
              <button className="fdn-icon-btn" type="button" aria-label="Notifications">
                <Svg>{ICON.bell}</Svg>
                <span className="fdn-dot" />
              </button>

              <div className="fdn-user-wrap">
                <button
                  className={`fdn-user ${userMenu ? "is-open" : ""}`}
                  type="button"
                  onClick={this.toggleUserMenu}
                  aria-haspopup="true"
                  aria-expanded={userMenu}
                >
                  <span className="fdn-user-avatar">{initial}</span>
                  <span className="fdn-user-meta">
                    <span className="fdn-user-name">{name}</span>
                    <span className="fdn-user-role">{classLabel || "IIT Foundation"}</span>
                  </span>
                  <span className="fdn-user-caret"><Svg>{ICON.caret}</Svg></span>
                </button>

                {userMenu && (
                  <>
                    <div className="fdn-menu-backdrop" onClick={this.closeUserMenu} />
                    <div className="fdn-user-menu" role="menu">
                      <div className="fdn-menu-head">
                        <span className="fdn-menu-avatar">{initial}</span>
                        <div className="fdn-menu-headinfo">
                          <span className="fdn-menu-headname">{name}</span>
                          {classLabel ? <span className="fdn-menu-headid">{classLabel}</span> : null}
                        </div>
                      </div>

                      {/* id + class are shown in the dashboard, not here */}
                      {email ? (
                        <div className="fdn-menu-rows">
                          <div className="fdn-menu-row"><span>Email</span><b>{email}</b></div>
                        </div>
                      ) : null}

                      <div className="fdn-menu-divider" />

                      <Link
                        to="/iit-foundation/login"
                        className="fdn-menu-item fdn-menu-danger"
                        role="menuitem"
                        onClick={this.handleLogout}
                      >
                        <Svg>{ICON.logout}</Svg>
                        <span>Logout</span>
                      </Link>
                    </div>
                  </>
                )}
              </div>
            </div>
            }
          />

          <div className="fdn-content">{children}</div>
        </div>
      </div>
    );
  }
}

export default withRouter(FoundationLayout);
